import { CoverBox } from '@/components/CoverBox/CoverBox';
import { convertLinkFormat } from '@/components/CoverBox/helpers';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ClassNameProvider } from '@/styles/CoverBox';
import { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { withDebugMetadata } from 'lib/utils';
import { renderContentComponent, standaloneComponent } from 'modules/dynamic-components';
import React, { ReactNode } from 'react';

export interface StandaloneCoverBoxProps extends StandaloneComponentProps<typeof CoverBox> {
  headline: string;
  bodyText: string;
  expandCopy?: ReactNode;
  icon?: ReactNode;
  images?: JSX.Element[];
}

export const StandaloneCoverBox: StandaloneComponent<StandaloneCoverBoxProps> = ({
  headline,
  bodyText,
  expandCopy,
  icon,
  images,
  options,
  ...props
}) => {
  const { $headline, $body, $group, $icon, $images, $expand, ...$base } = options ?? {};

  return (
    <ErrorBoundary>
      <ClassNameProvider value={{ ...$base, size: $base.size ?? $base.variant }}>
        <CoverBox {...$base} {...props}>
          {icon && <CoverBox.Icon {...$icon}>{icon}</CoverBox.Icon>}

          <CoverBox.Group {...$group}>
            <CoverBox.Headline {...$headline}>{headline}</CoverBox.Headline>

            <CoverBox.Body
              expand={<CoverBox.Expand {...$expand}>{expandCopy ?? <>&nbsp;...Läs mer</>}</CoverBox.Expand>}
              markup={bodyText}
              {...$body}
            >
              {images && images.length > 0 && (
                <CoverBox.Images {...$images}>
                  {images.map((image, index) => React.cloneElement(image, { key: index }))}
                </CoverBox.Images>
              )}
            </CoverBox.Body>
          </CoverBox.Group>
        </CoverBox>
      </ClassNameProvider>
    </ErrorBoundary>
  );
};

const DynamicStandaloneCoverBox = standaloneComponent('ContentCoverBox') as typeof standalone;

const LabradorCoverBox: LabradorComponent = ({ type, meta, data, descendants }) => {
  const images = descendants.map(renderContentComponent);

  return (
    <DynamicStandaloneCoverBox
      headline={data.title}
      bodyText={convertLinkFormat(data.bodytext || '')}
      images={images}
      options={data.options || {}}
      data-content-index={meta.contentIndex}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export const standalone = StandaloneCoverBox;

export default LabradorCoverBox;
